import { GLOSARIO } from './glosario';
/**
 * Manual de uso de Alexia Rest, pantalla a pantalla.
 *
 * Es UNA fuente para tres cosas (Alex, 5 sep 2026):
 *   - el panel de ayuda de cada pantalla (texto, preguntas frecuentes, vídeos),
 *   - el tour guiado que se hace al terminar el cuestionario inicial,
 *   - el prompt de Alexia cuando se le pregunta desde ese panel: sabe en qué
 *     pantalla está el usuario y cómo funciona, no solo qué datos tiene.
 *
 * Vive en `shared` porque lo leen el frontend y la API. Los vídeos los graba
 * Alex (ver VIDEOS más abajo); una pantalla sin vídeos no enseña la sección.
 *
 * Textos de todas las pantallas con manual: los de Alex (mejoras 62 y 68,
 * 6 sep 2026).
 *
 * Cómo se escribe: tuteando, frases cortas, sin jerga. Primero qué se hace
 * aquí, luego cómo, luego lo que suele fallar. Cada `howItWorks` es un
 * párrafo; las FAQ contestan en dos o tres frases.
 */

export interface HelpVideo {
  title: string;
  /** Enlace de YouTube (oculto) o Loom. El panel lo convierte a embed. */
  url: string;
  /** Duración legible, p. ej. «3 min». */
  duration?: string;
}

export interface HelpFaq {
  q: string;
  a: string;
}

export interface HelpScreen {
  /** Identificador estable: es lo que viaja a la API en el chat. */
  id: HelpScreenId;
  /** Rutas del frontend que pertenecen a esta pantalla (prefijo). */
  paths: string[];
  /** Nombre tal y como sale en el menú. */
  title: string;
  /** Una o dos frases: para qué sirve. */
  summary: string;
  /** Cómo funciona, por párrafos. */
  howItWorks: string[];
  faqs: HelpFaq[];
  /** Preguntas para el chat de esta pantalla. */
  suggestions: string[];
  videos: HelpVideo[];
}

export type HelpScreenId =
  | 'panel'
  | 'asistente'
  | 'borradores'
  | 'ingredientes'
  | 'inventario'
  | 'proveedores'
  | 'recetas'
  | 'precios'
  | 'ingresos'
  | 'gastos'
  | 'analisis'
  | 'resultados'
  | 'inversiones'
  | 'objetivos'
  | 'impuestos'
  | 'ajustes';

/**
 * Dónde viven los videotutoriales. Los graba Alex y se suben UNA vez al
 * bucket de ayuda de producción (`npm run ayuda:videos -- <carpeta>` en
 * infra); CloudFront los sirve por /ayuda/* en app.alexiarest.com. La demo
 * apunta aquí también: es el único sitio donde están.
 */
export const HELP_VIDEO_BASE = 'https://app.alexiarest.com/ayuda/videos';

const video = (pantalla: string, slug: string, title: string, duration: string): HelpVideo => ({
  title,
  url: `${HELP_VIDEO_BASE}/${pantalla}/${slug}.mp4`,
  duration,
});

/** Tutoriales por pantalla (documento «Tutoriales de Alexia» de Alex, 6 sep 2026). */
const VIDEOS: Partial<Record<HelpScreenId, HelpVideo[]>> = {
  ingresos: [
    video('ingresos', 'entender-ingresos', 'Cómo entender la sección de Ventas', '1 min'),
    video('ingresos', 'registrar-ingresos', 'Cómo registrar ingresos manualmente', '1 min'),
    video('ingresos', 'importar-ventas-excel', 'Cómo importar tus ventas desde Excel', '2 min'),
    video('ingresos', 'conectar-tpv', 'Cómo conectar tu punto de venta con Alexia', '1 min'),
  ],
  gastos: [
    video('gastos', 'entender-gastos', 'Cómo entender la sección de Gastos', '1 min'),
    video('gastos', 'registrar-gasto', 'Cómo registrar un gasto manualmente', '1 min'),
    video('gastos', 'escanear-factura', 'Cómo escanear una factura con Alexia', '1 min'),
    video('gastos', 'facturas-pendientes', 'Cómo gestionar facturas pendientes y cuentas por pagar', '1 min'),
  ],
  ingredientes: [
    video('ingredientes', 'crear-ingrediente', 'Cómo crear un ingrediente', '1 min'),
    video('ingredientes', 'importar-ingredientes-excel', 'Cómo importar tus ingredientes desde Excel', '2 min'),
    video('ingredientes', 'actualizar-precios', 'Cómo actualizar los precios de tus ingredientes', '1 min'),
    video('ingredientes', 'gestionar-ingredientes', 'Cómo gestionar tu base de ingredientes', '1 min'),
  ],
  recetas: [
    video('recetas', 'plato-y-subreceta', 'Diferencia entre un plato y una preparación', '1 min'),
    video('recetas', 'crear-subreceta', 'Cómo crear y costear una preparación', '2 min'),
    video('recetas', 'merma', 'Cómo funciona la merma en una receta', '1 min'),
    video('recetas', 'crear-plato', 'Cómo crear y costear un plato final', '1 min'),
    video('recetas', 'importar-recetas-excel', 'Cómo importar recetas y preparaciones desde Excel', '1 min'),
    video('recetas', 'ficha-tecnica', 'Cómo crear una ficha técnica de producción', '1 min'),
  ],
  precios: [
    video('precios', 'anadir-plato-carta', 'Cómo agregar un plato a tu carta y ponerle precio', '1 min'),
    video('precios', 'rentabilidad-plato', 'Cómo entender el análisis de rentabilidad de un plato', '1 min'),
    video('precios', 'cambios-coste-platos', 'Cómo revisar cambios en el costo de tus platos', '1 min'),
    video('precios', 'precios-por-canal', 'Cómo comparar precios y márgenes por canal', '1 min'),
  ],
  resultados: [
    video('resultados', 'leer-cuenta-resultados', 'Cómo leer tu estado de resultados', '1 min'),
    video('resultados', 'evolucion-rentabilidad', 'Cómo analizar la evolución de tu rentabilidad', '1 min'),
  ],
  inversiones: [
    video('inversiones', 'que-es-inversion', 'Qué es una inversión y cómo afecta a tus resultados', '1 min'),
    video('inversiones', 'registrar-inversion', 'Cómo registrar una inversión', '1 min'),
    video('inversiones', 'cuadro-depreciacion', 'Cómo leer el cuadro de depreciación', '1 min'),
  ],
  analisis: [
    video('analisis', 'punto-equilibrio-indicadores', 'Cómo entender tu punto de equilibrio y tus indicadores', '1 min'),
    video('analisis', 'cargar-ventas-por-plato', 'Cómo cargar las ventas por plato', '1 min'),
    video('analisis', 'ingenieria-menu', 'Cómo analizar tu ingeniería de menú', '2 min'),
  ],
  inventario: [
    video('inventario', 'recuento-inventario', 'Cómo hacer tu recuento de inventario', '2 min'),
    video('inventario', 'inventario-hoja-recuento', 'Cómo hacer el inventario con una hoja de recuento', '1 min'),
  ],
  asistente: [video('asistente', 'alexia-tu-asistente', 'Pregúntale a Alexia', '2 min')],
  impuestos: [video('impuestos', 'repartir-impuestos', 'Cómo repartir impuestos entre periodos', '2 min')],
  proveedores: [video('proveedores', 'gestionar-proveedores', 'Cómo gestionar tus proveedores', '1 min')],
  objetivos: [video('objetivos', 'definir-objetivos', 'Cómo definir y revisar tus objetivos', '1 min')],
  ajustes: [
    { title: 'Cómo modificar los datos de tu restaurante y facturación', url: `${HELP_VIDEO_BASE}/ajustes/restaurante.mp4` },
    { title: 'Cómo gestionar varios locales y repartir los gastos comunes', url: `${HELP_VIDEO_BASE}/ajustes/locales.mp4` },
    { title: 'Cómo configurar tus parámetros de venta, categorías y alertas', url: `${HELP_VIDEO_BASE}/ajustes/parametros.mp4` },
    { title: 'Cómo conectar tu punto de venta con Alexia', url: `${HELP_VIDEO_BASE}/ajustes/integraciones.mp4` },
    { title: 'Cómo gestionar tu plan, facturas y módulos', url: `${HELP_VIDEO_BASE}/ajustes/plan.mp4` },
    { title: 'Cómo invitar a tu equipo y gestionar sus permisos', url: `${HELP_VIDEO_BASE}/ajustes/equipo.mp4` },
    { title: 'Cómo gestionar tu cuenta y contraseña', url: `${HELP_VIDEO_BASE}/ajustes/cuenta.mp4` },
  ],
  panel: [video('panel', 'usar-panel-general', 'Cómo usar el panel general', '1 min')],
};

export const HELP_SCREENS: HelpScreen[] = [
  {
    id: 'panel',
    paths: ['/'],
    title: 'Panel general',
    summary:
      'La foto del negocio en el periodo que elijas arriba: ventas, costos y rentabilidad, con semáforo frente a tus objetivos.',
    howItWorks: [
      'El panel general reúne los principales datos de tu negocio para que puedas entender rápidamente qué está pasando en el periodo seleccionado.',
      'Aquí puedes ver indicadores de ventas, costos y rentabilidad, como ingresos, ticket promedio, costo de materia prima, costo de personal, Prime Cost, EBITDA, utilidad neta y margen de seguridad.',
      'Si has definido objetivos, Alexia compara tus resultados con ellos para mostrarte qué métricas están dentro de objetivo y cuáles necesitan atención.',
      'En «Necesita tu atención» encontrarás avisos importantes, como facturas vencidas, cambios en el costo de tus platos o márgenes que se han salido de los límites que has configurado. Cada aviso te lleva directamente al lugar donde puedes revisarlo.',
      'También encontrarás preguntas sugeridas para Alexia y diferentes gráficos para analizar la evolución de tus ingresos y gastos, el rendimiento por día de la semana, tus costos fijos y variables y los platos que más están aportando al negocio.',
      'La idea es sencilla: entrar, entender cómo va el negocio y saber dónde merece la pena mirar primero.',
    ],
    faqs: [
      {
        q: '¿Qué periodo estoy viendo?',
        a: 'Todos los datos del panel corresponden al periodo seleccionado en la parte superior. Puedes utilizar periodos predefinidos o elegir directamente las fechas que quieras analizar.',
      },
      {
        q: '¿Por qué algunos indicadores aparecen en verde y otros en rojo?',
        a: 'Cuando tienes un objetivo definido, Alexia compara el resultado real con ese objetivo. El verde indica que la métrica está dentro del objetivo y el rojo que necesita atención. Ten en cuenta que no todos los objetivos funcionan igual: en ventas normalmente quieres estar por encima, mientras que en costos normalmente quieres mantenerte por debajo.',
      },
      {
        q: '¿De dónde salen los datos del panel?',
        a: 'El panel utiliza la información que tienes registrada en las distintas secciones de Alexia. Por ejemplo, las ventas vienen de Ventas, los costos y gastos de Gastos, el consumo de materia prima también puede tener en cuenta el recuento mensual, y los costos de los platos provienen de Ingredientes, Recetas y Precios de la carta. Si falta información en alguna sección, algunos indicadores también pueden estar incompletos.',
      },
      {
        q: '¿Qué significa «Necesita tu atención»?',
        a: 'Es una selección de situaciones que Alexia considera conveniente que revises. Por ejemplo, una factura vencida, un plato cuyo costo ha cambiado significativamente o un margen que ha caído por debajo del límite que habías definido. No significa necesariamente que tengas que hacer un cambio: sirve para ayudarte a encontrar rápidamente dónde mirar.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: GLOSARIO.primeCost.texto,
      },
      {
        q: '¿Qué es el margen de seguridad?',
        a: GLOSARIO.margenSeguridad.texto,
      },
      {
        q: '¿Qué significa «Los platos que más aportan»?',
        a: 'Son los platos ordenados según su contribución total al negocio. No solo importa cuánto margen deja una unidad, sino también cuántas unidades se venden. Por eso un plato con un margen unitario menor puede aportar más dinero al negocio si se vende muchas más veces.',
      },
      {
        q: '¿Por qué puedo vender más y aun así ganar menos?',
        a: 'Porque las ventas son solo una parte del resultado. También pueden haber aumentado el costo de materia prima, el costo de personal, otros gastos o haberse reducido tus márgenes. Puedes revisar Estado de resultados o preguntarle directamente a Alexia para identificar qué ha cambiado.',
      },
      {
        q: '¿Qué puedo preguntarle a Alexia desde el panel?',
        a: 'Puedes utilizar las preguntas que Alexia te propone o hacer las tuyas. Por ejemplo: ¿por qué ha bajado mi ganancia?, ¿qué facturas tengo vencidas?, ¿qué costos se han disparado?, ¿qué platos debería revisar?, ¿voy a tener problemas de caja? Si quieres profundizar, abre Alexia y continúa allí la conversación.',
      },
      {
        q: '¿Tengo que revisar todo el panel cada día?',
        a: 'No. La idea del panel es precisamente evitarlo. Empieza por «Necesita tu atención» y por aquellos indicadores que estén fuera de tus objetivos. A partir de ahí puedes profundizar solo en aquello que realmente necesite revisión.',
      },
    ],
    suggestions: [
      '¿Cómo va el mes comparado con el anterior?',
      '¿Qué es lo que más me está costando?',
      '¿Qué significa el margen de seguridad?',
    ],
    videos: VIDEOS.panel ?? [],
  },
  {
    id: 'asistente',
    paths: ['/alexia', '/chat', '/asistente'],
    title: 'Alexia',
    summary:
      'Una sola conversación para tu restaurante: cuéntale un plato, un gasto o mándale una factura y te lo deja preparado para confirmar; pregúntale cómo va el negocio y te responde con tus números.',
    howItWorks: [
      'Alexia es una conversación, como un chat. No tienes que elegir qué quieres hacer antes de escribir: ella entiende si le estás contando algo o preguntando algo.',
      'Si le cuentas cómo haces un plato («el risotto lo hago con 320 g de arroz, 200 g de setas y medio litro de caldo, salen cuatro porciones»), prepara la receta con su costeo: qué ingredientes lleva, cuánto cuesta cada porción y qué margen te deja al precio de la carta. Puedes escribirlo o dictarlo con el botón «Dictar».',
      'Si le cuentas un gasto («he pagado 24 € de pan») o le adjuntas una factura o un ticket con «Adjuntar», prepara el gasto y, si es una factura de proveedor, una propuesta aparte con los precios nuevos de tus ingredientes.',
      'Todo lo que le cuentas queda como propuesta pendiente: la ves en la propia conversación con los botones «Guardar», «Revisar» y «Descartar». Nada se guarda en tu restaurante hasta que tú lo confirmas. Las propuestas caducan a los siete días.',
      'Si le falta el precio de un ingrediente, no se para: lo estima y lo marca en ámbar con una ~. Así ves un margen aproximado desde el primer minuto y lo afinas cuando confirmes el precio con tu proveedor o con una factura.',
      'Si le preguntas por el negocio («¿estoy ganando dinero?», «¿qué facturas tengo vencidas?», «¿qué plato me deja menos margen?»), responde con los datos que tienes registrados, del periodo y el local seleccionados arriba. Bajo cada respuesta puedes abrir «Ver de dónde salen estos datos» para comprobar qué ha consultado.',
      'También puedes preguntarle cómo se usa la propia aplicación: cómo importar ventas, hacer un inventario o crear una preparación.',
      'La conversación se conserva siete días y es la misma en la computadora y en el celular. En el celular, Alexia ocupa toda la pantalla, como una app de mensajes.',
    ],
    faqs: [
      {
        q: '¿Tengo que decirle si es una receta, un gasto o una pregunta?',
        a: 'No. Escribe con tus palabras y Alexia lo entiende. Si no queda claro, te lo dirá y podrás aclararlo en el siguiente mensaje.',
      },
      {
        q: '¿Alexia puede cambiar mis datos?',
        a: 'Solo lo que tú confirmes. Lo que le cuentas se queda como propuesta pendiente hasta que pulsas «Guardar» o lo revisas y confirmas. Al responder preguntas únicamente consulta tus datos: no crea, cambia ni borra nada.',
      },
      {
        q: '¿Qué pasa si un ingrediente no tiene precio?',
        a: 'Alexia lo estima y lo marca en ámbar con una ~, para que puedas ver un costo y un margen aproximados desde el principio. Es una aproximación, nunca un precio real: confírmalo en Ingredientes, con una factura o con tu proveedor, y el margen deja de ser estimado.',
      },
      {
        q: '¿Cómo le mando una factura?',
        a: 'Haz clic en «Adjuntar» en la conversación y elige la foto (JPEG o PNG) o el PDF, de hasta 8 MB. Indica si es una factura o una receta y envía. En el celular, con la app instalada, también puedes compartir la foto desde la galería o la cámara directamente a Alexia: Compartir → Alexia. Cada factura gasta un escaneo de tu plan.',
      },
      {
        q: '¿Dónde veo todo lo que tengo pendiente de confirmar?',
        a: 'En la propia conversación, junto a cada mensaje, y también en «Borradores pendientes», donde puedes confirmar varios de golpe. En el panel general y en Gastos y Recetas aparece un aviso con cuántos hay.',
      },
      {
        q: '¿Puedo corregir una receta que entendió mal?',
        a: 'Sí. Haz clic en «Revisar» para editar cada línea (ingrediente, cantidad, unidad y merma) antes de guardar, o «Aclarar receta» y escribe lo que cambia («no, salen seis porciones»). Alexia rehace la propuesta sin guardar nada.',
      },
      {
        q: '¿Qué periodo está analizando cuando le pregunto?',
        a: 'El periodo y el local seleccionados en la parte superior de la pantalla. Lo ves también en la cabecera de la conversación. Si quieres analizar otro mes, cambia las fechas antes de preguntar.',
      },
      {
        q: '¿Cuántos mensajes puedo enviarle?',
        a: 'Cada plan incluye un número de mensajes al mes (cada mensaje cuenta uno, sea una receta, un gasto o una pregunta) y un número de escaneos de facturas. Lo ves en Configuración → Tu plan. Si te quedas sin mensajes, Alexia te lo dice y puedes ampliar el plan; si te quedas sin escaneos, puedes comprar 150 más para ese mes.',
      },
      {
        q: '¿Puedo confiar siempre en lo que me responde?',
        a: 'Alexia utiliza inteligencia artificial y puede equivocarse. Por eso todo lo que propone se revisa antes de guardarse, y bajo cada respuesta puedes ver de qué datos ha salido. Usa sus respuestas como apoyo y revisa las cifras antes de tomar decisiones importantes.',
      },
      {
        q: '¿Qué hago si Alexia no resuelve mi duda?',
        a: 'Reformula la pregunta con más detalle. Y si sigues necesitando ayuda, escríbenos desde Soporte: te atiende una persona.',
      },
    ],
    suggestions: [
      '¿Qué puedo contarte y qué puedo preguntarte?',
      '¿Cuántos mensajes me quedan este mes?',
      '¿Cómo corrijo una receta que has entendido mal?',
    ],
    videos: VIDEOS.asistente ?? [],
  },
  {
    id: 'borradores',
    paths: ['/borradores'],
    title: 'Por confirmar',
    summary:
      'Todo lo que le has contado a Alexia y todavía no has confirmado: recetas, gastos y precios de factura, vengan de la app, del correo o del celular.',
    howItWorks: [
      'Cada cosa que le cuentas a Alexia se convierte en un borrador: una receta con su costeo, un gasto o una lista de precios nuevos de una factura. Aquí están todos los que siguen pendientes.',
      'Haz clic en «Revisar y confirmar» para abrir un borrador, corregir lo que haga falta y guardarlo. Al guardarlo pasa a ser una receta o un gasto normal de tu restaurante.',
      'Si hay varios borradores listos (sin dudas ni duplicados), puedes confirmarlos de golpe con «Confirmar N borradores listos». Los que necesitan una decisión tuya se revisan uno a uno.',
      'Puedes crear un ingrediente desde una factura: en la revisión de productos elige «Crear ingrediente nuevo», confirma su nombre, proveedor, presentación y precio sin IVA. Los precios que vienen en una factura se confirman por separado del gasto: así puedes registrar la factura sin tocar los precios de tus ingredientes, o al revés.',
      '«Descartar» elimina la propuesta sin guardar nada. Un borrador que no se toca caduca a los siete días.',
      '«Copiar enlace» crea un enlace de 15 minutos para abrir ese borrador en otro dispositivo; requiere iniciar sesión.',
    ],
    faqs: [
      {
        q: '¿Por qué hay un borrador de gasto y otro de precios de la misma factura?',
        a: 'Porque son dos decisiones distintas. El gasto es lo que has pagado y va a tu estado de resultados. Los precios actualizan tus ingredientes y, con ellos, el costo de tus recetas. Puedes confirmar uno sin el otro.',
      },
      {
        q: '¿Qué significa que un borrador «necesita revisión»?',
        a: 'Que Alexia no ha podido cerrarlo sola: le falta el IVA, hay un ingrediente que puede ser uno de varios de tu catálogo, o parece un duplicado de un gasto que ya tienes. Ábrelo, decide y guarda.',
      },
      {
        q: '¿Se pierde algo si un borrador caduca?',
        a: 'La propuesta desaparece, pero tu restaurante no cambia: nada se había guardado. Vuelve a contárselo a Alexia o a adjuntar la factura.',
      },
      {
        q: '¿Quién puede confirmar borradores?',
        a: 'Quien tenga permiso para editar esa sección: Recetas para las recetas, Gastos para los gastos y los precios. Un acceso de solo lectura ve la conversación pero no confirma nada.',
      },
    ],
    suggestions: ['¿Qué borradores tengo pendientes?', '¿Por qué un borrador necesita revisión?'],
    videos: [],
  },
  {
    id: 'ingredientes',
    paths: ['/ingredientes'],
    title: 'Ingredientes',
    summary:
      'El catálogo de lo que compras, con un solo precio por ingrediente: el vigente y sin IVA. De aquí sale el costo de cada plato.',
    howItWorks: [
      'Aquí puedes crear y mantener tu base de datos de ingredientes.',
      'Para cada ingrediente debes indicar cómo lo compras realmente: proveedor, formato de compra, cantidad, unidad y precio. Por ejemplo, una garrafa de aceite de 5 litros o una caja de 24 botellas.',
      'Puedes introducir el precio con IVA o sin IVA. Alexia calculará automáticamente el otro y utilizará siempre el precio sin IVA para calcular el costo de tus recetas.',
      'Si tienes muchos ingredientes, puedes importarlos de forma masiva desde Excel. Y cuando necesites actualizar precios, puedes descargar tu catálogo, modificar únicamente los precios que hayan cambiado y volver a subirlo.',
      'Cada vez que actualices el precio de un ingrediente, Alexia recalculará automáticamente el costo de las recetas y platos que lo utilizan.',
      'También puedes organizar tus ingredientes por grupos. Si necesitas crear grupos nuevos, puedes hacerlo desde Configuración.',
    ],
    faqs: [
      {
        q: '¿Qué tengo que poner como formato de compra?',
        a: 'Indica cómo compras realmente ese producto a tu proveedor. Por ejemplo: una caja, una bolsa, una botella, una garrafa o un saco. Después indica cuánto producto contiene ese formato y en qué unidad.',
      },
      {
        q: '¿Y si compro un ingrediente a granel?',
        a: 'Selecciona «a granel» como formato e indica la unidad en la que lo compras, por ejemplo kilos o litros, junto con su precio.',
      },
      {
        q: '¿Puedo introducir el precio con IVA?',
        a: 'Sí. Puedes introducir el precio con IVA o sin IVA. Alexia calculará automáticamente el otro utilizando el porcentaje de IVA que indiques. Para el cálculo de tus recetas se utilizará siempre el precio sin IVA.',
      },
      {
        q: '¿Qué IVA pongo en «IVA de la compra»?',
        a: 'El que te cobra el proveedor en esa compra, que puede cambiar de un producto a otro o de una factura a otra. No es el IVA que configuraste al dar de alta tu restaurante: ese es el que tú cobras en tus ventas y se utiliza en «Precios de la carta». Son dos cosas distintas y por eso Alexia no llena ninguna de las dos con la otra.',
      },
      {
        q: '¿Para qué sirve la densidad?',
        a: 'La densidad permite convertir correctamente entre unidades de peso y volumen. Puede ser útil, por ejemplo, en líquidos como aceites o salsas cuando una receta utiliza gramos pero tú compras el producto en litros. Es un dato opcional y no necesitas utilizarlo en todos los ingredientes.',
      },
      {
        q: 'Compro el mismo ingrediente a dos proveedores con precios distintos, ¿qué hago?',
        a: 'Mantén en Alexia el precio que quieras utilizar como referencia para calcular tus costos (recomendación: usa el más caro siempre que puedas). Si cambias de proveedor o cambia el precio de compra, puedes editar el ingrediente y actualizarlo.',
      },
      {
        q: '¿Qué pasa cuando cambio el precio de un ingrediente?',
        a: 'Alexia recalcula automáticamente el costo de todas las recetas y platos que utilizan ese ingrediente. Si el cambio provoca una desviación importante en algún plato, podrá aparecerte una alerta según los límites que tengas configurados.',
      },
      {
        q: '¿Cómo actualizo muchos precios a la vez?',
        a: 'Haz clic en «Actualizar ingredientes» y descarga tu catálogo. Modifica en el archivo únicamente los precios que hayan cambiado, guárdalo y vuelve a subirlo a Alexia. Antes de aplicar los cambios, podrás revisar qué ingredientes se van a actualizar.',
      },
      {
        q: '¿Puedo borrar un ingrediente que ya está utilizado en recetas?',
        a: 'No: si el ingrediente forma parte de alguna receta o preparación, Alexia no te permitirá borrarlo y te dirá en cuáles está. Quítalo de esas recetas primero, o cámbialo por otro ingrediente, y después podrás eliminarlo.',
      },
    ],
    suggestions: [
      '¿Cómo meto un precio que tengo con IVA?',
      '¿Qué ingredientes han subido de precio últimamente?',
      '¿Cómo actualizo todos los precios de golpe?',
    ],
    videos: VIDEOS.ingredientes ?? [],
  },
  {
    id: 'inventario',
    paths: ['/inventario'],
    title: 'Recuento mensual',
    summary:
      'El recuento de lo que te queda en el almacén al cerrar el mes. Con él, la estado de resultados enseña lo que has consumido, no solo lo que has comprado.',
    howItWorks: [
      'Aquí puedes registrar cuánto producto te queda al cierre de cada periodo para que Alexia calcule correctamente tu consumo real de materia prima.',
      'Lo que compras durante un mes no es necesariamente lo mismo que consumes. Por eso, Alexia utiliza tus compras junto con las existencias iniciales y finales para calcular cuánto producto has utilizado realmente y reflejarlo en tu estado de resultados.',
      'Puedes hacer el recuento directamente en Alexia indicando cuánto te queda de cada ingrediente, ya sea por unidades como kilos, litros o piezas, o utilizando su formato de compra: cajas, garrafas, sacos, botellas, etc.',
      'También puedes descargar una hoja de recuento, rellenarla en papel y volver a subirla para que Alexia lea las cantidades. Antes de guardar nada, siempre podrás revisarlas.',
      'Una vez guardado el inventario, podrás consultarlo después desde Historial. Cada recuento conserva el valor que tenían los ingredientes en ese momento.',
    ],
    faqs: [
      {
        q: '¿Por qué es importante hacer inventario?',
        a: 'Porque comprar producto no significa haberlo consumido. Si compras 5.000 $ de materia prima pero terminas el mes con parte todavía en almacén, no tendría sentido considerar los 5.000 $ como consumo. El inventario permite que Alexia calcule cuánto producto has consumido realmente durante el periodo.',
      },
      {
        q: '¿Tengo que contar todos los ingredientes?',
        a: 'Lo ideal es registrar todos los productos que tengan existencias al cierre. Los ingredientes que dejes a cero no se incluirán en el valor final del inventario. Si quieres que el consumo de materia prima sea lo más preciso posible, cuanto más completo sea el recuento, mejor.',
      },
      {
        q: '¿Qué pasa si no hago inventario?',
        a: 'Alexia no podrá ajustar las compras por las existencias que todavía tienes en almacén. Por tanto, tu estado de resultados puede mostrar como consumo producto que realmente todavía no has utilizado.',
      },
      {
        q: '¿Puedo contar por cajas, garrafas o sacos?',
        a: 'Sí. Activa «Contar por formato de compra» y podrás introducir cantidades como 3 cajas, 1,5 garrafas o 2 sacos. Alexia hará automáticamente la conversión utilizando el formato que tengas configurado en cada ingrediente.',
      },
      {
        q: '¿Puedo contar directamente en kilos, litros o piezas?',
        a: 'Sí. Puedes desactivar el recuento por formato de compra e introducir directamente la cantidad que te queda utilizando la unidad del ingrediente.',
      },
      {
        q: 'No me aparece un ingrediente para contar, ¿qué reviso?',
        a: 'Comprueba primero que el ingrediente esté correctamente dado de alta dentro de Ingredientes. Si sigue sin aparecer, puedes preguntarle a Alexia o escribirnos desde Soporte para revisarlo.',
      },
      {
        q: '¿Puedo hacer el inventario en papel?',
        a: 'Sí. Haz clic en «Hoja para imprimir» para generar una hoja de recuento con tus ingredientes. Después puedes completar el inventario en papel y volver a cargarlo desde «Subir hoja llenada».',
      },
      {
        q: '¿Alexia guarda automáticamente lo que lee de la hoja?',
        a: 'No. Alexia preparará las cantidades que haya podido interpretar, pero antes de guardar el recuento tendrás una pantalla de revisión para corregir cualquier dato.',
      },
      {
        q: '¿Qué pasa si cambia el precio de un ingrediente después de hacer el inventario?',
        a: 'El inventario histórico mantiene el valor que tenía el ingrediente en el momento en que guardaste ese recuento. Actualizar después el precio de compra no modifica el valor de inventarios anteriores.',
      },
      {
        q: '¿Dónde veo inventarios anteriores?',
        a: 'En la pestaña Historial. Ahí puedes consultar los recuentos que hayas guardado anteriormente y revisar su valor.',
      },
      {
        q: '¿Cómo afecta el inventario a mi estado de resultados?',
        a: 'Alexia utiliza las existencias iniciales, las compras del periodo y las existencias finales para calcular el consumo de materia prima. De esta forma, la estado de resultados refleja lo que realmente has consumido y no simplemente todo lo que has comprado.',
      },
    ],
    suggestions: [
      '¿Cómo afecta el inventario a la estado de resultados?',
      '¿Cómo subo la hoja de recuento llenada?',
      '¿Cuánto consumí de materia prima el mes pasado?',
    ],
    videos: VIDEOS.inventario ?? [],
  },
  {
    id: 'proveedores',
    paths: ['/proveedores'],
    title: 'Proveedores',
    summary: 'La lista de a quién compras. Se usa al dar de alta ingredientes y al registrar gastos.',
    howItWorks: [
      'Aquí puedes guardar y consultar todos los proveedores que utilizas dentro de Alexia.',
      'Puedes crear un proveedor directamente desde esta sección o hacerlo mientras registras un gasto o das de alta un ingrediente. En ambos casos quedará guardado automáticamente en tu lista.',
      'Para cada proveedor puedes agregar datos de contacto y notas internas, como horarios de pedido, días de entrega, condiciones especiales o cualquier información útil para tu equipo.',
      'También puedes ver cuántos ingredientes tienes asociados a cada proveedor (y hacer clic en en ese número para verlos), buscar uno concreto y editar su información cuando lo necesites.',
      'La idea es que tengas toda la información de tus proveedores centralizada en un mismo lugar.',
    ],
    faqs: [
      {
        q: '¿Tengo que crear los proveedores desde esta sección?',
        a: 'No. Puedes crearlos directamente desde Proveedores, pero también mientras registras un gasto o das de alta un ingrediente. Si creas uno desde otra sección, aparecerá automáticamente aquí.',
      },
      {
        q: '¿Qué información puedo guardar de un proveedor?',
        a: 'Puedes guardar sus datos de contacto y agregar notas internas. Por ejemplo, quién es tu persona de contacto, teléfono, días de reparto, horarios para hacer pedidos, condiciones especiales o cualquier información que te ayude a trabajar con ese proveedor.',
      },
      {
        q: '¿Para qué sirve el número de ingredientes que aparece junto al proveedor?',
        a: 'Indica cuántos ingredientes tienes actualmente asociados a ese proveedor dentro de Alexia. Púlsalo y verás cuáles son en Ingredientes, con el filtro de ese proveedor ya aplicado.',
      },
      {
        q: '¿Puedo cambiar el proveedor de un ingrediente?',
        a: 'Sí. Edita el ingrediente correspondiente y selecciona el nuevo proveedor. La información del proveedor se gestiona desde esta sección, pero la relación con cada producto se cambia desde Ingredientes.',
      },
      {
        q: '¿Puedo editar un proveedor después de crearlo?',
        a: 'Sí. Haz clic en Editar para modificar sus datos de contacto, notas o cualquier otra información disponible.',
      },
      {
        q: '¿Puedo borrar un proveedor?',
        a: 'Sí, siempre que no esté asignado a ningún ingrediente. Si lo está, cambia primero esos ingredientes de proveedor y después podrás eliminarlo.',
      },
      {
        q: '¿Puedo tener dos proveedores para el mismo ingrediente?',
        a: 'Actualmente cada ingrediente tiene un proveedor asociado como referencia. Si cambias de proveedor, puedes editar el ingrediente y seleccionar el nuevo.',
      },
    ],
    suggestions: [
      '¿A qué proveedor le compro más?',
      '¿Qué facturas tengo pendientes con un proveedor?',
    ],
    videos: VIDEOS.proveedores ?? [],
  },
  {
    id: 'recetas',
    paths: ['/carta', '/preparaciones', '/recetas'],
    title: 'Tu carta y Preparaciones',
    summary:
      'Tu carta es lo que vendes: lo que te cuesta cada plato, a cuánto lo vendes y lo que te deja. Las preparaciones son lo que haces en cantidad y usas dentro de los platos: salsas, masas, caldos.',
    howItWorks: [
      'En Tu carta cada fila es un plato: Te cuesta · Lo vendes a · Te deja · Costo. Toca la fila para abrir su ficha.',
      '«Te deja» es el precio sin IVA menos la comisión (tarjeta o app) y el costo del plato. Verde si llega a tu mínimo y ámbar si no; el mínimo de cada canal se pone en Objetivos.',
      'Arriba eliges «Precios de: En el local» o cada app. En las apps el empaque siempre cuenta en el costo; en el local, solo si lo marcas en el plato.',
      'Las vistas rápidas te llevan directo a lo que hay que mirar: Te dejan poco, Les subió el costo y Sin terminar (sin precio o con el costo incompleto).',
      'La pestaña «Qué plato te conviene más» cruza lo que vendes de cada plato con lo que te deja y te dice qué hacer con cada uno.',
      'En Preparaciones ves cuánto rinde cada una, lo que te cuesta por kilo, litro o pieza y en qué platos se usa. «Se usa en: ninguno» avisa de las olvidadas.',
      'Para crear un plato o una preparación, cuéntaselo a Alexia (dictado, escrito o foto) o llénalo tú. Cuando cambia el precio de un ingrediente, todo se recalcula solo.',
    ],
    faqs: [
      {
        q: '¿Cuál es la diferencia entre un plato y una preparación?',
        a: 'Un plato es la receta final que después puedes poner en tu carta. Una preparación es una elaboración que utilizas dentro de uno o varios platos, como una salsa, un alioli, una masa o un fondo. Siempre que puedas, es recomendable crear primero las preparaciones y después utilizarlas para construir los platos finales.',
      },
      {
        q: '¿Cuál es la diferencia entre rendimiento y porciones?',
        a: 'El rendimiento indica cuánto producto obtienes al preparar la receta completa. Por ejemplo, una salsa puede rendir 800 gramos. Las porciones indican cuántas unidades de servicio obtienes de esa preparación. Dependiendo del tipo de receta, puedes utilizar uno u otro dato para calcular el costo que necesitas.',
      },
      {
        q: '¿Cuándo calculo por rendimiento y cuándo por porción?',
        a: 'Utiliza costo por rendimiento cuando después vayas a utilizar esa elaboración por peso o volumen. Por ejemplo, una crema que utilizarás en cantidades de 30 gramos dentro de diferentes platos. Utiliza costo por porción cuando quieras saber cuánto cuesta cada unidad que vas a servir o vender.',
      },
      {
        q: '¿Cómo funciona la merma?',
        a: 'La merma representa la parte del ingrediente que compras pero que no utilizas en esa preparación. Si necesitas 60 gramos de hojas de cilantro pero tienes que comprar 100 gramos porque descartas los tallos, Alexia tendrá en cuenta esa diferencia para calcular el costo real. La merma se define según cómo utilizas ese ingrediente en esa receta, por eso puede ser diferente en distintas preparaciones.',
      },
      {
        q: '¿La pérdida de peso durante la cocción es merma?',
        a: 'En esta sección, la merma se utiliza para reflejar principalmente la merma de preelaboración: pieles, tallos, huesos u otras partes que descartas antes de cocinar. No debes utilizarla simplemente para reflejar la pérdida de peso que ocurre durante la cocción.',
      },
      {
        q: 'Me sale un error de unidades al costear, ¿qué reviso?',
        a: 'Comprueba que la unidad que estás utilizando sea compatible con la forma en la que tienes registrado el ingrediente. Por ejemplo, peso con peso o volumen con volumen. Si necesitas convertir entre peso y volumen, como ocurre con algunos líquidos, puede ser necesario indicar la densidad del ingrediente.',
      },
      {
        q: '¿Qué pasa si cambia el precio de un ingrediente?',
        a: 'Alexia recalculará automáticamente el costo de todas las recetas y preparaciones que utilizan ese ingrediente. Si una preparación cambia de costo, ese cambio también se trasladará a los platos que la utilizan.',
      },
      {
        q: '¿Puedo incluir el empaque en el costo de un plato?',
        a: 'Sí. Puedes agregar costos de empaque, como envases o bolsas de delivery, para que formen parte del costo total del plato sin tratarlos como ingredientes de la preparación.',
      },
      {
        q: '¿Tengo que teclear los ingredientes uno a uno?',
        a: 'No. Cuéntale el plato a Alexia con tus palabras («300 g de arroz, 200 g de setas, medio litro de caldo, cuatro porciones»), por escrito o dictado, o súbele una foto de la receta. Ella reconoce los ingredientes de tu catálogo, propone los nuevos y monta el costeo; tú corriges lo que haga falta y guardas.',
      },
      {
        q: '¿Puedo importar mis recetas desde Excel?',
        a: 'Sí. Desde la opción de importar puedes descargar una plantilla y cargar de forma masiva tanto platos como preparaciones. Cada fila representa uno de los componentes de la receta y, antes de guardar nada, Alexia te mostrará si existe algún dato que necesitas revisar.',
      },
      {
        q: '¿Puedo crear fichas técnicas desde Alexia?',
        a: 'Sí. Dentro de cada receta puedes agregar el procedimiento, una fotografía y los alérgenos. Después puedes descargar una ficha técnica con la información de producción para imprimirla o compartirla con tu equipo.',
      },
    ],
    suggestions: [
      '¿Cómo pongo la merma de un ingrediente?',
      '¿Qué receta me cuesta más por porción?',
      '¿Cómo creo una preparación y la uso en un plato?',
    ],
    videos: VIDEOS.recetas ?? [],
  },
  {
    id: 'precios',
    paths: [],
    title: 'Precios de la carta',
    summary:
      'Qué margen deja cada plato en cada canal, descontando el IVA, la comisión de cobro y la de cada plataforma. Un plato rentable en sala puede perder dinero en delivery.',
    howItWorks: [
      'Aquí puedes agregar tus platos a la carta, ponerles precio y analizar cuánto margen te deja cada uno.',
      'Cada plato se enlaza con una receta ya creada, y puedes definir su nombre en carta, grupo, precio de venta, IVA y objetivo de food cost.',
      'Si vendes por distintos canales, puedes poner un precio diferente para cada uno. Alexia tendrá en cuenta las comisiones de cobro y delivery para calcular el margen real que te deja cada plato.',
      'Cuando cambia el costo de una receta, Alexia compara el nuevo costo con el que tenías como referencia y te avisa si hay una desviación importante.',
      'También puedes consultar el precio sugerido en función de tu objetivo de food cost y comparar tus márgenes entre canales.',
    ],
    faqs: [
      {
        q: '¿El precio de venta se introduce con IVA o sin IVA?',
        a: 'El precio de venta se introduce con IVA, tal y como lo ve el cliente en la carta. Alexia calculará automáticamente el precio sin IVA para realizar los análisis de margen y rentabilidad.',
      },
      {
        q: '¿Tengo que poner el mismo precio en el local y en delivery?',
        a: 'No. Puedes definir un precio distinto para cada canal. Alexia tendrá en cuenta las comisiones asociadas a cada uno para que puedas comparar cuánto margen te deja realmente el mismo plato en sala, Uber u otras plataformas.',
      },
      {
        q: '¿Por qué un plato puede tener buen margen en sala y malo en delivery?',
        a: 'Porque en delivery pueden existir comisiones adicionales que reducen el margen. Aunque el costo de la receta sea el mismo, el resultado puede cambiar mucho dependiendo de la comisión de cada plataforma y del precio que hayas puesto en ese canal.',
      },
      {
        q: '¿Qué significa el objetivo de food cost?',
        a: 'Es el porcentaje de costo sobre venta que te gustaría conseguir para ese plato. Alexia utiliza ese objetivo para calcular un precio de referencia que te permita acercarte al food cost que has definido. No significa que tengas que utilizar obligatoriamente ese precio.',
      },
      {
        q: '¿Qué pasa cuando cambia el costo de un plato?',
        a: 'Cuando cambia el precio de alguno de sus ingredientes o preparaciones, Alexia recalcula automáticamente el costo del plato. Si el cambio supera los límites que tienes configurados, te avisará para que puedas revisarlo.',
      },
      {
        q: '¿Qué significa aceptar el nuevo costo?',
        a: 'Significa que has revisado el cambio y quieres utilizar ese costo como nueva referencia. A partir de ese momento, Alexia comparará los futuros cambios con ese nuevo costo. Aceptar el costo no obliga a cambiar el precio de venta.',
      },
      {
        q: '¿Por qué Alexia me avisa de que un plato tiene poco margen?',
        a: 'Porque el margen del plato está por debajo del mínimo que tienes configurado como referencia. La alerta no significa necesariamente que tengas que subir el precio. Sirve para señalarte qué platos merece la pena revisar.',
      },
      {
        q: '¿Qué puedo hacer si un plato tiene poco margen?',
        a: 'Puedes revisar varias cosas: el costo de la receta, las cantidades utilizadas, el precio de venta o las comisiones del canal. Si quieres profundizar, también puedes preguntarle a Alexia y analizar con ella qué opciones tienes para mejorar el resultado.',
      },
    ],
    suggestions: [
      '¿Qué platos pierden dinero en delivery?',
      '¿A qué precio tendría que vender este plato para un food cost del 30 %?',
      '¿Qué platos tienen el margen más bajo?',
    ],
    videos: VIDEOS.precios ?? [],
  },
  {
    id: 'ingresos',
    paths: ['/ingresos'],
    title: 'Ventas',
    summary:
      'Lo que vendiste cada día, anotado al cerrar con tu corte de caja. Es la base de todo lo financiero.',
    howItWorks: [
      'Cada día es una fila, el más reciente arriba. Un día sin datos sale como «Sin anotar» (o «Cerrado» si ese día cierras), nunca como $0.',
      'Toca «Anotar el día de hoy» (o cualquier día) y anota lo que dice tu corte de caja: efectivo, tarjeta y otros; los pedidos para llevar y de cada app; clientes y mesas. Se guarda solo al escribir.',
      'Anótalo con IVA, tal cual sale en el corte: Alexia le quita el IVA y guarda el importe sin IVA. Si tu corte ya viene sin IVA, cámbialo en Configuración.',
      '¿Prefieres no teclear? Usa «Foto del corte» o «Dictar» y Alexia llena los campos. Lo que llena sale en verde para que lo revises.',
      'Arriba ves lo que vendiste en el mes, tus clientes y cuánto gasta cada uno. Los tickets solo usan los días que tienen ese dato. Lo de cómo te pagaron y por canal está en Análisis.',
      'También puedes importar el informe de tu punto de venta con «Importar ventas», o conectarlo para que las ventas entren solas cada noche.',
    ],
    faqs: [
      {
        q: '¿Las ventas se anotan con IVA o sin IVA?',
        a: 'Como prefieras, una sola vez, en Configuración. Por defecto se anotan con IVA, tal cual salen en el corte de caja, y Alexia guarda el importe sin IVA (importe ÷ (1 + IVA)). Todos los números de Alexia van sin IVA.',
      },
      {
        q: '¿El «para llevar» se suma a la caja?',
        a: 'No. Lo de para llevar ya se cobró en tu caja (efectivo o tarjeta): al anotarlo solo lo separas, para que «Cada cliente» sea de quien comió aquí. Vendiste = caja + apps.',
      },
      {
        q: '¿Qué días cuentan como «Sin anotar»?',
        a: 'Los días pasados sin ventas que no están marcados como cerrados. Pon en Configuración los días que cierras siempre, y usa «Este día cerré» para un festivo.',
      },
      {
        q: '¿Las cortesías y los descuentos se restan de las ventas?',
        a: 'No. Las cortesías y los descuentos se muestran como información para que puedas controlar cuánto dinero estás destinando a estos conceptos, pero no se vuelven a restar del total de ventas.',
      },
      {
        q: '¿Qué hago si mi punto de venta no está integrado con Alexia?',
        a: 'Puedes seguir cargando tus ventas manualmente o importar un archivo Excel o CSV desde la opción «Importar ventas». También puedes escribirnos desde Soporte para solicitar una nueva integración con tu punto de venta.',
      },
      {
        q: '¿Qué ocurre si importo ventas de un día que ya tiene datos?',
        a: 'Antes de guardar la importación, Alexia te avisará de que ese día ya contiene información. Podrás revisar el resumen y decidir si quieres sustituir los datos existentes o no.',
      },
    ],
    suggestions: [
      '¿Cómo importo las ventas de mi punto de venta?',
      '¿Qué día de la semana vendo más?',
      '¿Cuál es mi ticket medio este mes?',
    ],
    videos: VIDEOS.ingresos ?? [],
  },
  {
    id: 'gastos',
    paths: ['/gastos'],
    title: 'Gastos',
    summary:
      'Todo lo que pagas, clasificado en el plan de cuentas del restaurante y con su estado: pagado o pendiente, con factura o sin ella.',
    howItWorks: [
      'Aquí puedes registrar y consultar los gastos de tu negocio, controlar qué tienes pendiente de pagar y ver cómo se distribuyen entre costos fijos y variables.',
      'Puedes registrar un gasto manualmente, subir una foto o PDF al escáner de facturas, o contárselo a Alexia en su conversación («he pagado 24 € de pan») o adjuntarle allí la factura: ella prepara el gasto y tú lo confirmas. Si es una factura de proveedor, te propone aparte los precios nuevos de tus ingredientes.',
      'Cada gasto debe tener una categoría, un proveedor y un importe. Los importes se registran sin IVA, ya que son los que Alexia utilizará para construir tu estado de resultados.',
      'Si el gasto es a crédito, puedes agregar su fecha de vencimiento. Así Alexia podrá mostrarte qué facturas siguen pendientes y cuáles ya han vencido.',
      'También puedes indicar si la factura ya ha sido recibida o sigue pendiente y actualizar su estado más adelante.',
      'Las categorías que aparecen por defecto se pueden personalizar desde Configuración.',
    ],
    faqs: [
      {
        q: '¿Puedo importar gastos desde un Excel?',
        a: 'Sí. Haz clic en «Importar gastos», sube un CSV o Excel con una fila por gasto y di qué es cada columna (o pídeselo a Alexia). Solo son obligatorias la fecha y el importe; la categoría se reconoce por su nombre o por palabras del concepto, y lo que no se reconozca entra en la categoría por defecto que elijas. Si el archivo trae una columna «Local», cada fila va a su local.',
      },
      {
        q: '¿Cómo sé si un gasto es fijo o variable?',
        a: 'Un costo fijo normalmente no cambia directamente en función de cuánto vendas, como el alquiler o determinados salarios. Un costo variable sí suele aumentar o disminuir con la actividad del negocio, como las materias primas. Alexia te sugerirá una clasificación según la categoría que elijas, pero puedes modificarla si lo necesitas.',
      },
      {
        q: '¿Los gastos se registran con IVA o sin IVA?',
        a: 'En Alexia, los gastos se registran sin IVA para que ese sea el importe que se refleje en tu estado de resultados. Si utilizas el escáner de facturas, Alexia puede leer también el IVA para ayudarte con el registro, pero el análisis de resultados utilizará el importe sin IVA.',
      },
      {
        q: 'El IVA que me sale aquí no es el que configuré al darme de alta, ¿por qué?',
        a: 'Porque son dos cosas distintas. El IVA que configuraste al dar de alta tu restaurante es el que tú cobras en tus ventas. El de esta pantalla es «IVA de la compra»: el que te cobra a ti el proveedor en esa factura, y puede ser diferente en cada gasto. Alexia no lo llena por ti para que pongas siempre el de la factura que tienes delante, y puedes dejarlo vacío: el gasto se guarda sin IVA. Ten en cuenta que ese dato solo se utiliza en Tesorería; tu estado de resultados va siempre sin IVA.',
      },
      {
        q: '¿Qué diferencia hay entre factura pendiente y pago pendiente?',
        a: 'Son dos cosas diferentes. Una factura puede estar pendiente porque todavía no has recibido el documento del proveedor. Y un pago puede estar pendiente porque ya tienes la factura, pero todavía no la has pagado. Alexia te permite controlar ambos estados por separado.',
      },
      {
        q: '¿Cómo marco una factura como pagada?',
        a: 'Abre el gasto correspondiente y cambia el estado del pago de «Pendiente» a «Pagado». No necesitas volver a registrar el gasto.',
      },
      {
        q: '¿Qué pasa si una factura vence y todavía no la he pagado?',
        a: 'Si registraste el gasto a crédito y agregaste una fecha de vencimiento, Alexia podrá identificar cuándo el pago sigue pendiente y cuándo ya está vencido. Lo verás dentro de Cuentas por pagar.',
      },
      {
        q: '¿Puedo crear mis propias categorías de gasto?',
        a: 'Sí. Alexia incluye categorías por defecto, pero puedes crear o modificar tus propias categorías y subcategorías desde Configuración.',
      },
      {
        q: '¿Una inversión, como un horno o una reforma, se registra como gasto?',
        a: 'Normalmente no debería cargarse completa como un gasto corriente del periodo. Las inversiones se registran desde la sección Inversiones, donde Alexia puede distribuir su costo a lo largo de su vida útil mediante la depreciación.',
      },
      {
        q: '¿Dónde registro la nómina?',
        a: 'Dentro de Gastos, selecciona la categoría correspondiente a Personal y la subcategoría que corresponda, por ejemplo sueldos y salarios o seguridad social. Esos datos después aparecerán agrupados dentro del costo de personal en tu estado de resultados.',
      },
      {
        q: '¿Qué pasa si todavía no tengo la factura del proveedor?',
        a: 'Puedes registrar igualmente el gasto y marcar el estado como «Factura pendiente» o «Sin factura». Cuando recibas la factura, abre el gasto y cambia su estado a «Factura recibida».',
      },
      {
        q: 'Me llegó un abono o una nota de crédito del proveedor, ¿cómo lo registro?',
        a: 'Un gasto no admite importes en negativo. Abre la factura a la que corrige el abono y baja su importe por lo que te abonan; así tu estado de resultados queda bien. Si el abono no corrige una factura concreta, réstalo de la siguiente factura de ese proveedor y apúntalo en las notas del gasto.',
      },
    ],
    suggestions: [
      '¿Qué facturas tengo vencidas?',
      '¿En qué categoría de gasto me he pasado este mes?',
      '¿Cómo escaneo una factura?',
    ],
    videos: VIDEOS.gastos ?? [],
  },
  {
    id: 'analisis',
    paths: ['/analisis'],
    title: 'Análisis',
    summary:
      'Qué platos aportan más al negocio (ingeniería de menú) y cuánto necesitas vender para no perder dinero (punto de equilibrio).',
    howItWorks: [
      'El punto de equilibrio sale de tus costos fijos y del margen de contribución del periodo: es la venta mínima para cubrirlo todo. El margen de seguridad dice cuánto podrían caer las ventas antes de llegar ahí.',
      'La ingeniería de menú clasifica cada plato por grupo de carta según lo que se vende y el margen que deja: Estrella (se vende y deja margen), Caballo de batalla (se vende mucho, deja poco), Puzzle (buen margen, pocas ventas) y Perro (poco de ambas). Cada cuadrante trae su recomendación.',
      'Para clasificar hacen falta las unidades vendidas por plato. Si tu punto de venta está conectado, se traen con el botón sobre el periodo que estás mirando. Si no, se importan de un archivo.',
    ],
    faqs: [
      {
        q: '¿Por qué un plato sale como Perro?',
        a: 'Porque, comparado con los de su mismo grupo de carta, se vende poco y deja poco margen. No lo quites sin más: mira si comparte ingredientes, si cumple una función (infantil, alergias) o si el precio está mal.',
      },
      {
        q: 'No me sale la matriz.',
        a: 'Faltan las unidades vendidas del periodo. Tráelas del punto de venta o impórtalas; sin ellas no se puede saber qué se vende.',
      },
    ],
    suggestions: [
      '¿Qué platos son estrella y cuáles debería revisar?',
      '¿Cuánto tengo que vender al mes para cubrir costos?',
      '¿Cómo cargo las unidades vendidas por plato?',
    ],
    videos: VIDEOS.analisis ?? [],
  },
  {
    id: 'resultados',
    paths: ['/resultados'],
    title: 'Estado de resultados',
    summary:
      'De las ventas al utilidad neta, paso a paso: materia prima, personal, gastos generales, EBITDA, depreciaciones y resultado.',
    howItWorks: [
      'Aquí puedes ver cómo tus ventas se convierten en ganancia a medida que se descuentan los diferentes costos y gastos del negocio.',
      'Selecciona el periodo que quieras analizar. Alexia construirá automáticamente la estado de resultados con los ingresos, gastos e inversiones que tengas registrados.',
      'En la cascada del resultado puedes ver de forma rápida el recorrido desde los ingresos hasta el utilidad neta.',
      'Más abajo tienes el detalle completo, donde puedes desplegar partidas como consumo de materia prima, costo de personal y gastos generales para ver qué está provocando cada resultado.',
      'Además del importe, Alexia te muestra qué porcentaje de tus ingresos representa cada partida.',
      'Al final de la página puedes comparar tus resultados mes a mes y ver cómo evolucionan tus ingresos, EBITDA, utilidad neta y rentabilidad.',
    ],
    faqs: [
      {
        q: '¿De dónde salen los datos de esta estado de resultados?',
        a: 'Alexia utiliza la información que tienes registrada en otras secciones. Las ventas vienen de Ventas, los gastos de Gastos y las depreciaciones de Inversiones. Si falta información en alguna de estas secciones, también afectará al resultado que veas aquí.',
      },
      {
        q: '¿Por qué la ganancia no coincide con el dinero que tengo en el banco?',
        a: 'Porque ganancia y tesorería no son lo mismo. La estado de resultados mide si el negocio ha generado ganancia durante un periodo, pero el saldo del banco también depende de cuándo cobras y pagas, préstamos, inversiones, impuestos y otros movimientos de dinero. Por eso puedes tener ganancia y no tener ese mismo importe disponible en la cuenta bancaria.',
      },
      {
        q: '¿Qué es el margen bruto?',
        a: 'Es lo que queda de tus ventas después de descontar el costo directamente relacionado con lo que vendes, principalmente el consumo de materia prima. Te ayuda a entender cuánto dinero queda disponible para cubrir el resto de gastos del negocio.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: GLOSARIO.primeCost.texto,
      },
      {
        q: '¿Qué es el EBITDA?',
        a: `«${GLOSARIO.teDejo.palabra}» (EBITDA): ${GLOSARIO.teDejo.texto}`,
      },
      {
        q: '¿Qué diferencia hay entre EBITDA, EBIT y utilidad neta?',
        a: `«${GLOSARIO.teDejo.palabra}» (EBITDA): ${GLOSARIO.teDejo.texto} El EBIT ya no sale en el Estado de resultados. «${GLOSARIO.gananciaNeta.palabra}»: ${GLOSARIO.gananciaNeta.texto}`,
      },
      {
        q: '¿Por qué aparece una depreciación si este mes no compré nada?',
        a: 'Porque las inversiones no se cargan completas en el mes en que se compran. Alexia distribuye su costo a lo largo de su vida útil y refleja periódicamente esa parte como depreciación. Puedes consultar el detalle desde Inversiones.',
      },
      {
        q: '¿Por qué las cortesías y descuentos aparecen pero no se restan?',
        a: 'Porque se muestran como información para que puedas controlar su peso, pero no se vuelven a descontar si las ventas que has registrado ya reflejan ese efecto. Así evitamos restarlos dos veces.',
      },
      {
        q: '¿Cómo sé qué está haciendo bajar mi ganancia?',
        a: 'Empieza comparando el periodo con otros meses y después revisa qué partidas han aumentado como porcentaje de ventas. Puedes desplegar materia prima, personal y gastos generales para encontrar dónde está la desviación. También puedes preguntarle directamente a Alexia para que te ayude a interpretar los cambios.',
      },
    ],
    suggestions: [
      '¿Por qué he ganado menos este mes que el anterior?',
      '¿Qué porcentaje de las ventas se va en personal?',
      '¿Cuál es mi EBITDA del año?',
    ],
    videos: VIDEOS.resultados ?? [],
  },
  {
    id: 'inversiones',
    paths: ['/inversiones'],
    title: 'Inversiones',
    summary:
      'Equipos, mobiliario y reformas. Su costo se reparte a lo largo de su vida útil y solo la parte del mes entra en resultados.',
    howItWorks: [
      'Das de alta la inversión con su importe, la fecha y los años de vida útil. Alexia calcula la cuota mensual de depreciación y la lleva a la estado de resultados cada mes, debajo del EBITDA.',
      'El cuadro de depreciación enseña, mes a mes, cuánto llevas depreciado y cuánto queda.',
    ],
    faqs: [
      {
        q: '¿Cuántos años pongo?',
        a: 'Orientativo: maquinaria de cocina 8-10 años, mobiliario 10, informática 4, reformas 10-20. Tu contador te dirá el criterio fiscal exacto; aquí importa que el mes cargue una parte razonable.',
      },
    ],
    suggestions: ['¿Cuánto amortizo al mes?', '¿Qué inversiones tengo activas?'],
    videos: VIDEOS.inversiones ?? [],
  },
  {
    id: 'objetivos',
    paths: ['/objetivos'],
    title: 'Objetivos',
    summary:
      'Lo que quieres conseguir cada mes: ventas, ticket, food cost, personal, prime cost y EBITDA. El panel y Alexia comparan el real con esto.',
    howItWorks: [
      'Aquí puedes definir los objetivos que quieres seguir cada mes y compararlos con los resultados reales de tu negocio.',
      'Selecciona el mes y completa únicamente las métricas que te interese controlar, como ventas, ticket promedio, EBITDA, compras, costo de materia prima, costo de personal, Prime Cost o margen de seguridad.',
      'No es necesario rellenarlas todas. Los campos que dejes en blanco simplemente no se incluirán en la comparación.',
      'Cuando guardes los objetivos, Alexia los comparará con los datos reales de ese mes y te dirá cuánto te falta o cuánto te pasas y si cada objetivo se está cumpliendo o no.',
      'Estos objetivos también se utilizan en el panel general para señalar qué métricas están dentro de objetivo y cuáles necesitan tu atención.',
      'Puedes definir objetivos diferentes para cada mes.',
    ],
    faqs: [
      {
        q: '¿Tengo que completar todos los objetivos?',
        a: 'No. Solo necesitas definir las métricas que realmente quieras seguir. Si dejas un campo en blanco, Alexia no lo incluirá en la comparación de ese mes.',
      },
      {
        q: '¿Puedo tener objetivos diferentes cada mes?',
        a: 'Sí. Los objetivos se guardan por mes, así que puedes adaptarlos a la temporada, al presupuesto o a la situación concreta de tu negocio.',
      },
      {
        q: '¿De dónde salen los resultados reales?',
        a: 'Alexia utiliza los datos que tienes registrados durante ese mes en las diferentes secciones de la plataforma. Por eso, si faltan ingresos, gastos u otra información necesaria, el resultado real también puede quedar incompleto.',
      },
      {
        q: '¿Qué significa «Te falta» o «Te pasas»?',
        a: `${GLOSARIO.teSeparaDelObjetivo.texto} El % va al lado del número: «Te faltan 1.200 (8 %)».`,
      },
      {
        q: '¿Por qué algunas métricas se cumplen al estar por encima y otras al estar por debajo?',
        a: 'Porque no todos los objetivos funcionan igual. Por ejemplo, normalmente quieres que las ventas o el EBITDA sean iguales o superiores al objetivo. En cambio, en métricas como costo de materia prima, costo de personal o Prime Cost, el objetivo suele funcionar como un máximo, por lo que se cumple cuando el resultado real es igual o inferior.',
      },
      {
        q: '¿Qué diferencia hay entre compras de materia prima y costo de materia prima?',
        a: 'Las compras de materia prima indican cuánto dinero has destinado a comprar producto durante el mes. El costo de materia prima refleja cuánto representa el consumo de materia prima sobre tus ventas. No son exactamente lo mismo, especialmente si trabajas con inventario.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: GLOSARIO.primeCost.texto,
      },
      {
        q: '¿Qué es el margen de seguridad?',
        a: GLOSARIO.margenSeguridad.texto,
      },
      {
        q: '¿Los objetivos cambian mis datos?',
        a: 'No. Los objetivos solo sirven como referencia para comparar tus resultados reales. No modifican ingresos, gastos, costos ni ninguna otra información de Alexia.',
      },
      {
        q: '¿Puedo cambiar un objetivo después de guardarlo?',
        a: 'Sí. Vuelve al mes correspondiente, modifica el valor que quieras y guarda de nuevo los objetivos.',
      },
    ],
    suggestions: [
      '¿Qué objetivos estoy cumpliendo este mes?',
      '¿Qué objetivo de ventas sería razonable para el mes que viene?',
    ],
    videos: VIDEOS.objetivos ?? [],
  },
  {
    id: 'impuestos',
    paths: ['/impuestos'],
    title: 'Impuestos',
    summary:
      'Reparte un pago trimestral o anual entre los meses a los que corresponde, para que el mes del pago no cargue con todo.',
    howItWorks: [
      'Aquí puedes repartir entre varios meses un pago de impuestos que hayas realizado en una sola fecha.',
      'Esto sirve para evitar que todo el importe afecte únicamente al mes en el que hiciste el pago cuando, en realidad, corresponde a varios periodos.',
      'Indica el concepto, la partida de gasto a la que corresponde, el importe pagado, la fecha del pago, el primer mes al que corresponde y cuántos meses quieres cubrir.',
      'Después selecciona la base de reparto. Puedes distribuir el importe según el peso de las ventas o de la nómina de cada mes.',
      'Alexia calculará cuánto corresponde a cada periodo y llevará esa parte a tu estado de resultados, en la partida que hayas elegido.',
      'Esta sección sirve para mejorar el análisis interno de tus resultados; no sustituye el criterio fiscal o contable que corresponda a cada impuesto.',
    ],
    faqs: [
      {
        q: '¿Por qué repartir un impuesto entre varios meses?',
        a: 'Porque la fecha en la que realizas el pago no siempre coincide con los meses a los que corresponde. Si pagas en septiembre un importe correspondiente a junio, julio y agosto, cargarlo entero en septiembre podría distorsionar la comparación entre meses.',
      },
      {
        q: '¿Qué significa «base de reparto»?',
        a: 'Es la referencia que Alexia utilizará para decidir qué parte del pago corresponde a cada mes. Puedes repartirlo según el peso de las ventas o de la nómina de cada periodo. No significa necesariamente que todos los meses reciban exactamente la misma cantidad.',
      },
      {
        q: '¿Cuándo utilizo ventas y cuándo nómina?',
        a: 'Utiliza la opción que mejor represente aquello con lo que está relacionado el pago. Si quieres distribuirlo según la actividad o facturación de cada mes, puedes utilizar Ventas. Si el pago está relacionado con el costo de personal, puedes utilizar Nómina.',
      },
      {
        q: '¿Alexia divide siempre el importe a partes iguales?',
        a: 'No necesariamente. Si seleccionas una base de reparto, Alexia tendrá en cuenta el peso que esa métrica tuvo en cada mes. Por ejemplo, un mes con más ventas puede recibir una parte mayor del importe que otro con menos ventas.',
      },
      {
        q: '¿Qué pongo como primer mes del periodo?',
        a: 'El primer mes al que realmente quieres atribuir parte de ese pago. Después indica cuántos meses cubre y Alexia te mostrará el periodo completo antes de repartirlo.',
      },
      {
        q: 'Pagué un trimestre completo de una sola vez, ¿lo registro todo en el mes del pago?',
        a: 'Si ese pago corresponde realmente a varios meses y quieres que tu análisis mensual refleje esa distribución, puedes utilizar esta sección para repartirlo entre los periodos correspondientes. Así evitas que un único mes quede artificialmente cargado con todo el importe.',
      },
      {
        q: '¿El IVA se registra aquí como un gasto?',
        a: 'El IVA tiene un tratamiento diferente al de un gasto corriente y no debería repartirse aquí automáticamente como si fuera un costo del negocio. Si tienes dudas sobre cómo debe tratarse en tu caso, consulta con tu asesor fiscal.',
      },
      {
        q: '¿Puedo cambiar un reparto después de hacerlo?',
        a: 'No directamente desde esta sección. Cuando repartes un pago, Alexia crea automáticamente los gastos correspondientes en los meses que hayas indicado. Si necesitas corregirlo, ve a Gastos, busca los movimientos generados en cada mes y elimínalos. Después puedes volver a Impuestos y hacer el reparto de nuevo con los datos correctos.',
      },
    ],
    suggestions: ['¿Cómo reparto el pago de un trimestre?', '¿Qué impuestos tengo periodificados este año?'],
    videos: VIDEOS.impuestos ?? [],
  },
  {
    id: 'ajustes',
    paths: ['/ajustes'],
    title: 'Configuración',
    summary:
      'Desde Configuración puedes configurar cómo funciona tu cuenta de Alexia y adaptarla a las necesidades de tu negocio: parámetros de venta, categorías, alertas, punto de venta, plan, facturas, equipo y acceso.',
    howItWorks: [
      'Mi restaurante: nombre, país, moneda, locales y los datos de facturación de tu suscripción.',
      'Parámetros de venta: el IVA de ventas, la comisión por cobro con tarjeta, las plataformas de delivery con su comisión, el sistema de medidas, los grupos de ingredientes y de gasto, y los umbrales de alerta (desviación de costo y margen bruto mínimo).',
      'Integraciones: conectar Loyverse o Square para que las ventas entren solas, decir a qué local va cada tienda y traer un rango de ventas.',
      'Tu plan: tu plan, lo que llevas gastado este mes (mensajes y escaneos), tus facturas, usuarios y locales adicionales, y la compra de 150 escaneos más para el mes.',
      'Equipo: invitar al encargado (ve la operación, no los resultados) o a tu contador (lo ve todo, no cambia nada).',
    ],
    faqs: [
      { q: '¿Dónde configuro el IVA, las comisiones de tarjeta y las plataformas de delivery?', a: 'En Parámetros de venta puedes definir el IVA habitual de tus ventas, la comisión de tarjeta y las plataformas de delivery. Configura la comisión de cada plataforma para que Alexia la tenga en cuenta en los cálculos.' },
      { q: '¿Puedo personalizar las categorías de ingredientes y gastos?', a: 'Sí. Desde Parámetros de venta puedes ocultar categorías que no utilices, crear grupos de ingredientes y agregar subgrupos dentro de tus partidas de gasto.' },
      { q: '¿Cómo funcionan las alertas de costos y margen?', a: 'Puedes indicar a partir de qué subida o bajada en el costo de un plato quieres recibir un aviso y establecer un margen bruto mínimo. Si un plato cae por debajo, Alexia te avisa para que puedas revisarlo.' },
      { q: '¿Qué hago si mi punto de venta no está integrado con Alexia?', a: 'En Integraciones puedes consultar los puntos de venta disponibles y las instrucciones para conectarlos. Si el tuyo no aparece, escríbenos desde Soporte. Mientras tanto, puedes cargar ventas manualmente o mediante Excel o CSV.' },
      { q: '¿Dónde puedo consultar mi plan y descargar mis facturas?', a: 'En Tu plan puedes ver tu plan, gestionar tu suscripción, consultar las facturas generadas, agregar usuarios o locales y comprar 150 escaneos más si te quedas corto un mes.' },
      { q: '¿Puedo controlar qué información ve cada miembro de mi equipo?', a: 'Sí. En Equipo puedes invitar usuarios y asignar roles según sus funciones. También puedes crear roles personalizados, cambiar permisos o desactivar el acceso de un usuario.' },
      { q: '¿Dónde puedo cambiar mi contraseña?', a: 'En Mi cuenta puedes consultar tu correo y tu rol y cambiar tu contraseña. Si entras con Google, la contraseña se gestiona desde tu cuenta de Google.' },
      { q: 'He cambiado el IVA, ¿se recalcula todo?', a: 'Los márgenes de la carta se recalculan con el nuevo IVA. Los ingresos ya registrados no cambian.' },
    ],
    suggestions: [
      '¿Qué umbral de alerta de costo me conviene?',
      '¿Cómo conecto mi punto de venta?',
      '¿Qué puede ver el encargado y qué no?',
    ],
    videos: VIDEOS.ajustes ?? [],
  },
];

/** Pantalla a la que pertenece una ruta del frontend, o undefined si no tiene manual. */
export function helpScreenForPath(pathname: string, search = ''): HelpScreen | undefined {
  const limpio = pathname.replace(/\/+$/, '') || '/';
  let mejor: HelpScreen | undefined;
  let largo = -1;
  for (const screen of HELP_SCREENS) {
    for (const path of screen.paths) {
      const coincide = path === '/' ? limpio === '/' : limpio === path || limpio.startsWith(`${path}/`);
      if (coincide && path.length > largo) {
        mejor = screen;
        largo = path.length;
      }
    }
  }
  if (mejor?.id === 'ajustes') {
    const tab = new URLSearchParams(search).get('tab') ?? (new URLSearchParams(search).get('pago') ? 'plan' : 'restaurante');
    const slugs = tab === 'restaurante' ? ['restaurante', 'locales'] : [tab];
    return { ...mejor, videos: mejor.videos.filter(v => slugs.some(slug => v.url.endsWith(`/${slug}.mp4`))) };
  }
  return mejor;
}

export function helpScreenById(id: string): HelpScreen | undefined {
  return HELP_SCREENS.find((screen) => screen.id === id);
}

/**
 * Manual de UNA pantalla en texto plano, para el prompt de Alexia. Va sin
 * los vídeos y sin las sugerencias: es lo que el modelo necesita para
 * contestar «cómo hago X aquí».
 */
/* ------------------------------------------------------------------ */
/* Con varios locales                                                  */
/* ------------------------------------------------------------------ */

/**
 * Ayuda del multi-local (7 sep 2026). Solo se ensenya cuando el negocio tiene
 * mas de un local activo: a un restaurante con uno no hay que hablarle de
 * selectores ni consolidados. La web la pinta en el panel de ayuda bajo «Con
 * varios locales» y Alexia la recibe en su manual con el mismo criterio.
 */
export const MULTI_LOCAL_INTRO: HelpFaq[] = [
  {
    q: '¿Cómo elijo el local que estoy viendo?',
    a: 'Con el selector «Local» de la cabecera, arriba a la izquierda. Lo que registres (ventas, gastos, inventario, inversiones, objetivos) entra en el local que tengas elegido. En las pantallas de análisis puedes elegir además «Todos los locales» para ver el consolidado del grupo.',
  },
  {
    q: '¿Qué es «Todos los locales»?',
    a: 'El consolidado del grupo: la suma de todos los locales. Está en el panel general, Análisis, Estado de resultados, Tesorería, Objetivos y Alexia. En las pantallas donde se registran datos no aparece, porque cada dato tiene que ir a un local concreto.',
  },
  {
    q: '¿Qué es común a todos los locales?',
    a: 'La carta, las recetas, los ingredientes y los proveedores: una sola carta y un solo costeo para todo el negocio. Los usuarios del equipo también ven todos los locales. Todo lo demás (ventas, gastos, tesorería, inventario, inversiones, objetivos) va local a local.',
  },
  {
    q: '¿Cómo agrego otro local?',
    a: 'En Configuración → Mi restaurante → Locales, «Agregar local». Todos los planes incluyen uno; cada local adicional se amplía desde ahí mismo. En cuanto lo agregas aparece en el selector «Local».',
  },
];

export const MULTI_LOCAL_FAQS: Partial<Record<HelpScreenId, HelpFaq[]>> = {
  panel: [
    {
      q: '¿Estoy viendo un local o el grupo?',
      a: 'Lo dice el selector «Local» de la cabecera. Con un local elegido, los indicadores, los avisos y los platos que más aportan son de ese local; con «Todos los locales», del grupo entero. Los objetivos que se comparan son los de ese local, o el objetivo del grupo compuesto con los de todos.',
    },
  ],
  ingresos: [
    {
      q: '¿En qué local se guardan las ventas?',
      a: 'En el que tengas elegido arriba, en «Local». Por eso aquí no aparece «Todos los locales»: cada día de ventas es de un local. Con el punto de venta conectado, cada tienda del punto de venta entra sola en su local (Configuración → Integraciones). Si importas un archivo, entra entero en el local elegido.',
    },
  ],
  gastos: [
    {
      q: '¿Qué es un gasto «común a todo el grupo»?',
      a: 'Un gasto que no es de ningún local en concreto: tu contador, la oficina, el gerente. Elige «Ámbito: común a todo el grupo» al registrarlo y se guarda una sola vez. La estado de resultados de cada local se lleva su parte, en proporción a las ventas del mes o con un porcentaje fijo por local (Configuración → Mi restaurante → Gastos comunes del grupo). En el consolidado entra entero.',
    },
    {
      q: '¿Por qué veo un gasto común en la lista de mi local?',
      a: 'Para que la factura se encuentre desde cualquier local: en la lista sale entera, con la etiqueta «Común al grupo». En la estado de resultados de ese local solo entra su parte.',
    },
    {
      q: '¿Y si importo un archivo con gastos de varios locales?',
      a: 'Agrega una columna «Local» con el nombre de cada local tal como está en Configuración. Cada fila irá al suyo, y «Grupo» o «Común» marca un gasto común. Sin esa columna, todo el archivo entra en el local elegido arriba.',
    },
  ],
  analisis: [
    {
      q: '¿Y si tengo varios locales?',
      a: 'Las unidades vendidas se registran local a local: elige el local arriba, en «Local», y teclea o importa las suyas. Con «Todos los locales» ves la suma y la matriz de ingeniería de menú del grupo; con un local, la de ese local. Con el punto de venta conectado, cada tienda suma en su local.',
    },
  ],
  resultados: [
    {
      q: '¿Cómo entran los gastos comunes en la cuenta de cada local?',
      a: 'Repartidos: cada local se lleva la parte que le toca según el reparto elegido en Configuración (por ventas del mes o porcentaje fijo). Con «Todos los locales» el gasto común entra entero, así que la suma de las cuentas de los locales cuadra con la del grupo.',
    },
  ],
  inventario: [
    {
      q: '¿El recuento es por local?',
      a: 'Sí. Cada local cuenta sus existencias con el local elegido arriba, y el consumo de materia prima de su estado de resultados usa su recuento. Los ingredientes y sus precios son los mismos para todos.',
    },
  ],
  inversiones: [
    {
      q: '¿Las inversiones van por local?',
      a: 'Sí. Cada inversión se registra en el local elegido arriba y su depreciación entra en la cuenta de ese local. Con «Todos los locales» (en Estado de resultados y Tesorería) se suman.',
    },
  ],
  objetivos: [
    {
      q: '¿Y si tengo varios locales?',
      a: 'Los objetivos se fijan local a local: elige el local arriba, en «Local», y guarda sus objetivos. Con «Todos los locales» ves el consolidado del grupo frente al real: las ventas y las compras se suman, y los porcentajes y tickets se ponderan por las ventas objetivo de cada local.',
    },
  ],
  impuestos: [
    {
      q: '¿En qué local entran los gastos de la periodificación?',
      a: 'En el que tengas elegido arriba: la periodificación crea un gasto por mes y cada uno tiene que ser de un local. Si el pago es de todo el grupo, regístralo después como gasto común desde Gastos.',
    },
  ],
  asistente: [
    {
      q: '¿Alexia responde sobre un local o sobre el grupo?',
      a: 'Sobre lo que tengas elegido en el selector «Local»: con un local, sus cifras; con «Todos los locales», las del grupo. Si quieres comparar dos locales, pregunta con uno elegido y luego con el otro, o pídele el consolidado.',
    },
  ],
  ajustes: [
    {
      q: '¿Cómo reparto los gastos comunes entre locales?',
      a: 'En Mi restaurante → Gastos comunes del grupo. Dos opciones: en proporción a las ventas de cada local en el mes del gasto (por defecto), o con un porcentaje fijo por local que sume 100 %.',
    },
    {
      q: '¿Cada local puede tener su propia cuenta de punto de venta?',
      a: 'Sí, y pueden ser distintos (por ejemplo, Centro con Revo y Norte con Loyverse). En Configuración › Cómo vendes › Tu punto de venta, elige el local y conecta el suyo: sus ventas van solo a ese local y se traen solas cada noche. Si los locales comparten cuenta, basta con decir a qué local va cada tienda. «Desconectar» desde un local solo suelta ese local.',
    },
  ],
};

/** Preguntas «Con varios locales» de una pantalla: la introduccion en el panel y en Ajustes, y las suyas. */
export function multiLocalFaqsFor(screenId: HelpScreenId): HelpFaq[] {
  const intro = screenId === 'panel' || screenId === 'ajustes' ? MULTI_LOCAL_INTRO : [];
  return [...intro, ...(MULTI_LOCAL_FAQS[screenId] ?? [])];
}

export function helpScreenAsText(screen: HelpScreen, options: { multiLocal?: boolean } = {}): string {
  const lineas = [
    `## ${screen.title}`,
    screen.summary,
    '',
    ...screen.howItWorks.map((p) => `- ${p}`),
  ];
  if (screen.faqs.length > 0) {
    lineas.push('', 'Preguntas frecuentes:');
    for (const faq of screen.faqs) lineas.push(`- ${faq.q} ${faq.a}`);
  }
  if (options.multiLocal) {
    const faqs = [...MULTI_LOCAL_INTRO, ...(MULTI_LOCAL_FAQS[screen.id] ?? [])];
    lineas.push('', 'Con varios locales (este negocio tiene más de uno):');
    for (const faq of faqs) lineas.push(`- ${faq.q} ${faq.a}`);
  }
  // Ayuda en cada pantalla (15 oct 2026): Alexia enlaza al vídeo de la pantalla.
  if (screen.videos.length > 0) {
    lineas.push('', 'Vídeos de esta pantalla (enlázalos con Markdown cuando ayuden):');
    for (const v of screen.videos) lineas.push(`- [${v.title}](${v.url})`);
  }
  return lineas.join('\n');
}

/**
 * Índice de una línea por pantalla, con su ruta: para que Alexia sepa mandar
 * a cada sitio y enlazarlo. Es estático: se calcula una vez.
 */
export const HELP_INDEX_TEXT: string = HELP_SCREENS.map((screen) => `- ${screen.title} (${screen.paths[0] ?? '/'}): ${screen.summary}`).join('\n');

/* ------------------------------------------------------------------ */
/* Ayuda en cada pantalla (Alex, 15 oct 2026)                           */
/* ------------------------------------------------------------------ */

/**
 * Las 3 preguntas de cada pantalla: las que más nos hacen por WhatsApp. Cada
 * semana se apunta qué se pregunta y desde qué pantalla; si algo se pregunta 3
 * veces, no se arregla aquí: se arregla la pantalla. Sin lista, las 3
 * primeras que no son «¿Qué es…?».
 */
export const PREGUNTAS_DESTACADAS: Partial<Record<HelpScreenId, string[]>> = {
  panel: [
    '¿De dónde salen los datos del panel?',
    '¿Por qué algunos indicadores aparecen en verde y otros en rojo?',
    '¿Por qué puedo vender más y aun así ganar menos?',
  ],
  gastos: [
    '¿Qué pasa si todavía no tengo la factura del proveedor?',
    '¿Cómo marco una factura como pagada?',
    '¿Una inversión, como un horno o una reforma, se registra como gasto?',
  ],
  resultados: [
    '¿Por qué la ganancia no coincide con el dinero que tengo en el banco?',
    '¿Cómo sé qué está haciendo bajar mi ganancia?',
    '¿Por qué aparece una depreciación si este mes no compré nada?',
  ],
};

/**
 * Las «¿Qué es…?» ya no van en la Ayuda: son un «?» junto a la palabra en la
 * pantalla (ver glosario.ts). Siguen en el manual de Alexia.
 */
export function esPreguntaDeQueEs(q: string): boolean {
  return /^¿Qué es (el|la) /i.test(q) || /^¿Qué diferencia hay entre EBITDA/i.test(q);
}

export interface AyudaDePantalla {
  /** UN vídeo de un minuto: el primero de la pantalla. */
  video?: HelpVideo;
  /** Los demás vídeos, dentro de «Ver todas». */
  masVideos: HelpVideo[];
  /** Las 3 que más nos preguntan. */
  destacadas: HelpFaq[];
  /** El resto, en «Ver todas». */
  resto: HelpFaq[];
}

/** Lo que enseña el panel de Ayuda de una pantalla, en el orden decidido con Alex. */
export function ayudaDePantalla(screen: HelpScreen): AyudaDePantalla {
  const faqs = screen.faqs.filter((f) => !esPreguntaDeQueEs(f.q));
  const lista = PREGUNTAS_DESTACADAS[screen.id] ?? [];
  const elegidas = lista.map((q) => faqs.find((f) => f.q === q)).filter((f): f is HelpFaq => Boolean(f));
  const destacadas = [...elegidas, ...faqs.filter((f) => !elegidas.includes(f))].slice(0, 3);
  return {
    video: screen.videos[0],
    masVideos: screen.videos.slice(1),
    destacadas,
    resto: faqs.filter((f) => !destacadas.includes(f)),
  };
}

export function helpIndexAsText(): string {
  return HELP_INDEX_TEXT;
}
